<template>
  <div class="about-root">

    <!-- ─── Hero Banner ─── -->
    <section class="hero-section">
      <img src="/logos/about3.webp" alt="About SupplemEarth" class="hero-img" fetchpriority="high" decoding="async" @load="(e) => (e.target as HTMLElement).classList.add('loaded')" />
      <div class="hero-overlay">
        <h1 class="hero-title">About us</h1>
      </div>
    </section>

    <!-- ─── Mission: about2 + text ─── -->
    <section class="section mission-section">
      <div class="mission-grid">
        <div class="mission-img-wrap reveal">
          <img src="/logos/about2.webp" alt="Our mission" class="mission-img" decoding="async" />
          <div class="img-accent"></div>
        </div>
        <div class="mission-text reveal delay-1">
          <span class="tag">Our Mission</span>
          <blockquote class="mission-quote">
            "At Supplemearth, we believe wellness should feel simple, natural and real."
          </blockquote>
          <p class="mission-body">
            Our mission is to help you nourish your body and mind, even in the middle of a busy life,
            with supplements made from earth and designed to fit into your everyday routine.
          </p>
          <div class="divider"></div>
        </div>
      </div>
    </section>

    <!-- ─── Founder's Note: text + about3 ─── -->
    <section class="section founder-section">
      <div class="founder-grid">
        <div class="founder-text reveal">
          <span class="tag tag--light">Founder's Note</span>
          <div class="founder-paragraphs">
            <p>We started SupplemEarth from our own need to find balance between work, life, and health.</p>
            <p>We created these products for people who live fast, dream big, and still want to stay grounded.</p>
            <p>We're grateful to share this journey with you, because this isn't just about supplements.</p>
            <p>It's about feeling your best from the inside out.</p>
          </div>
          <p class="signature">N &amp; J</p>
        </div>
        <div class="founder-img-wrap reveal delay-1">
          <img src="/logos/about1.webp" alt="Founders" class="founder-img" decoding="async" />
          <div class="img-accent img-accent--right"></div>
        </div>
      </div>
    </section>

    <!-- ─── CTA ─── -->
    <section class="cta-section reveal">
      <div class="cta-line"></div>
      <p class="cta-eyebrow">Ready to start?</p>
      <h2 class="cta-title">Find your balance.</h2>
      <RouterLink to="/products" class="cta-btn">
        Explore Products
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </RouterLink>
    </section>

  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { RouterLink } from 'vue-router'

onMounted(() => {
  const els = document.querySelectorAll('.reveal')
  const obs = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('in-view')
          obs.unobserve(e.target)
        }
      })
    },
    { threshold: 0.12 }
  )
  els.forEach((el) => obs.observe(el))
})
</script>

<style scoped>
/* ── Variables ── */
.about-root {
  --cream: #F3EDE2;
  --espresso: #1a1200;
  --sage: #5f4842;
  --gold: #EDD97A;
  --warm: #5f4842;
  --border: rgba(26, 18, 0, 0.12);

  font-family: "Roboto", sans-serif;
  background: var(--cream);
  color: var(--espresso);
}

/* ── Hero ── */
.hero-section {
  position: relative;
  width: 100%;
  height: calc(82vh + var(--header-height, 120px));
  min-height: 500px;
  margin-top: calc(-1 * var(--header-height, 120px));
  overflow: hidden;
}

.hero-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.hero-img.loaded {
  opacity: 1;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding: 0 clamp(1.5rem, 6vw, 7rem) clamp(2.5rem, 5vw, 5rem);
  padding-top: var(--header-height, 120px);
  background: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 50%);
}

.hero-title {
  font-family: 'Cormorant Garamond', serif;
  font-weight: 300;
  font-size: clamp(3.5rem, 9vw, 8rem);
  line-height: 0.95;
  margin: 0;
  color: #fff;
}

/* ── Shared section ── */
.section {
  padding: clamp(5rem, 10vw, 9rem) clamp(1.5rem, 6vw, 7rem);
}

.tag {
  display: inline-block;
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--sage);
  border-bottom: 1px solid var(--sage);
  padding-bottom: 0.3rem;
  margin-bottom: 2.5rem;
}

.tag--light {
  color: var(--gold);
  border-color: rgba(237, 217, 122, 0.5);
}

.divider {
  width: 3.5rem;
  height: 1px;
  background: var(--border);
  margin-top: 2.5rem;
}

/* ── Mission ── */
.mission-section {
  background: var(--cream);
  content-visibility: auto;
  contain-intrinsic-size: 0 600px;
}

.mission-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(3rem, 7vw, 8rem);
  align-items: center;
  max-width: 1280px;
  margin: 0 auto;
}

.mission-img-wrap {
  position: relative;
}

.mission-img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
}

.img-accent {
  position: absolute;
  bottom: -1.25rem;
  left: -1.25rem;
  width: 55%;
  height: 55%;
  border: 1.5px solid var(--gold);
  z-index: -1;
  pointer-events: none;
}

.img-accent--right {
  bottom: -1.25rem;
  left: auto;
  right: -1.25rem;
}

.mission-quote {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-weight: 300;
  font-size: clamp(1.6rem, 2.5vw, 2.4rem);
  line-height: 1.35;
  color: var(--espresso);
  margin: 0 0 1.75rem 0;
  padding: 0;
  border: none;
}

.mission-body {
  font-size: 1rem;
  line-height: 1.9;
  color: var(--warm);
  max-width: 40ch;
}

/* ── Founder ── */
.founder-section {
  background: var(--espresso);
  content-visibility: auto;
  contain-intrinsic-size: 0 600px;
}

.founder-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(3rem, 7vw, 8rem);
  align-items: center;
  max-width: 1280px;
  margin: 0 auto;
}

.founder-paragraphs {
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  margin-bottom: 0;
}

.founder-paragraphs p {
  font-size: 1rem;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.65);
}

.signature {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-weight: 400;
  font-size: 2.25rem;
  color: var(--gold);
  margin-top: 2.75rem;
  margin-bottom: 0;
}

.founder-img-wrap {
  position: relative;
}

.founder-img {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
}

/* ── CTA ── */
.cta-section {
  padding: clamp(6rem, 11vw, 11rem) clamp(1.5rem, 6vw, 7rem);
  text-align: center;
  background: var(--cream);
}

.cta-line {
  width: 1px;
  height: 5rem;
  background: var(--border);
  margin: 0 auto 3rem;
}

.cta-eyebrow {
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--sage);
  margin-bottom: 1.25rem;
}

.cta-title {
  font-family: 'Cormorant Garamond', serif;
  font-weight: 300;
  font-size: clamp(2.75rem, 6vw, 5.5rem);
  line-height: 1.05;
  margin: 0 0 3rem;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  background: var(--espresso);
  color: var(--gold);
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 1.1rem 2.75rem;
  text-decoration: none;
  transition: background 0.3s ease, gap 0.3s ease;
}

.cta-btn:hover {
  background: #2a2400;
  gap: 1.25rem;
}

/* ── Reveal animations ── */
.reveal {
  opacity: 0;
  transform: translateY(36px);
  transition: opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1),
              transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
}

.reveal.delay-1 {
  transition-delay: 0.18s;
}

.reveal.in-view {
  opacity: 1;
  transform: none;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .mission-grid,
  .founder-grid {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  .mission-img {
    aspect-ratio: 3 / 2;
  }

  .founder-img {
    aspect-ratio: 4 / 3;
  }

  .img-accent {
    display: none;
  }

  .hero-section {
    height: 75vh;
  }
}
</style>
